import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TRIPS = [
  {
    race: "Ladakh Marathon",
    city: "Leh",
    country: "India",
    dates: "Race day 13 Sep 2026 · 4-night trip",
    price: "₹42,000",
    img: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=80",
    highlights: ["High-altitude race-week planning", "Acclimatisation walks and local transfers", "Group stay near Leh market"],
  },
  {
    race: "Vedanta Delhi Half Marathon",
    city: "New Delhi",
    country: "India",
    dates: "Race day 18 Oct 2026 · 2-night trip",
    price: "₹12,500",
    img: "https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=1600&q=80",
    highlights: ["Well-organised city half marathon", "Hotel, expo and start-line logistics", "Group shakeout and finish-line meet-up"],
  },
  {
    race: "Tata Mumbai Marathon",
    city: "Mumbai",
    country: "India",
    dates: "Race day 17 Jan 2027 · 3-night trip",
    price: "₹15,000",
    img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?auto=format&fit=crop&w=1600&q=80",
    highlights: ["India's largest city marathon", "Sea-side accommodation options", "Crew coordination across race weekend"],
  },
  {
    race: "Chiang Mai Marathon",
    city: "Chiang Mai",
    country: "Thailand",
    dates: "Race week Dec 2026 · 4-night trip",
    price: "₹68,000",
    img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=1600&q=80",
    highlights: ["Friendly international destination race", "Airport transfers and old-city stay", "Group runs and recovery itinerary"],
  },
  {
    race: "Comrades Marathon",
    city: "KwaZulu-Natal",
    country: "South Africa",
    dates: "Race day Jun 2027 · 5-night trip",
    price: "Custom quote",
    img: "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=1600&q=80",
    highlights: ["Iconic point-to-point ultra", "Crew, pacing and transport planning", "Post-race recovery options"],
  },
];

export default function Travel() {
  const [trips, setTrips] = useState(TRIPS);

  useEffect(() => {
    axios
      .get(`${API}/travel`)
      .then((r) => {
        if (r.data.length) setTrips(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="travel" className="px-6 sm:px-12 lg:px-20 py-24 sm:py-32 lg:py-40 border-t border-[#0D1117]/10" data-testid="travel-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 sm:mb-20"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4E00] mb-5">Race Travel</p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#0D1117] tracking-tight max-w-2xl leading-tight">
            Run quality races in India & abroad.
            <span className="italic text-[#FF4E00]"> We handle travel, stay & logistics.</span>
          </h2>
        </div>
        <p className="text-sm sm:text-base text-[#374151] leading-relaxed max-w-md">
          Fitygrity plans the complete race journey — accommodation, airport transfers, local
          warm-up runs, bib coordination and group camaraderie for well-organised events at home
          and abroad.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {trips.map((t, i) => (
          <motion.article
            key={t.race}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
            data-testid="travel-card"
            className="group rounded-2xl border border-[#0D1117]/10 hover:border-[#FF4E00]/40 bg-[#FFFFFF] overflow-hidden transition-colors duration-500 flex flex-col"
          >
            <div className="relative aspect-[16/10] overflow-hidden">
              <img
                src={t.img}
                alt={`${t.race} — ${t.city}, ${t.country}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#FBF9F5] via-transparent to-transparent" />
              <span className="absolute top-5 left-5 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#0D1117] bg-[#FBF9F5]/70 backdrop-blur px-3 py-1.5 rounded-full">
                <MapPin className="w-3 h-3 text-[#FF4E00]" />
                {t.city}, {t.country}
              </span>
            </div>
            <div className="p-6 sm:p-7 flex flex-col flex-1">
              <h3 className="font-serif text-xl sm:text-2xl text-[#0D1117] group-hover:text-[#FF4E00] transition-colors duration-300">
                {t.race}
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4E00] mt-2.5 inline-flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5" /> {t.dates}
              </p>
              <p className="font-mono text-xs tracking-[0.1em] text-[#0D1117] mt-2">
                From <span className="text-[#FF4E00]">{t.price}</span>
                <span className="text-[#6B7280] text-[10px] uppercase tracking-[0.15em]"> · per runner</span>
              </p>
              <ul className="mt-4 space-y-2 flex-1">
                {t.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2.5 text-sm text-[#374151]">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF4E00] mt-1.5 shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
              <a
                href="#enquire"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("fg:enquire", { detail: `${t.race} (${t.dates})` })
                  )
                }
                data-testid="travel-enquire-button"
                className="group/btn mt-6 inline-flex items-center justify-center gap-2 border border-[#FF4E00]/50 hover:border-[#FF4E00] text-[#FF4E00] font-mono text-[11px] uppercase tracking-[0.2em] px-6 py-3.5 rounded-full transition-all duration-300 hover:bg-[#FF4E00] hover:text-[#FBF9F5]"
              >
                Enquire
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:rotate-45" />
              </a>
            </div>
          </motion.article>
        ))}
      </div>

      <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280] max-w-2xl leading-relaxed">
        Race availability is checked against IndiaRunning, iFinish, Ahotu, BookMyShow and official
        event sites — final travel dates, hotels and bib status confirmed on enquiry.
      </p>
    </section>
  );
}
